import GeoJSON from 'ol/format/GeoJSON';

export interface NormalizedUploadedGeoJsonResult {
  geojson: Record<string, unknown> | null;
  error: string | null;
  cleaned: boolean;
  notes: string[];
}

export interface NormalizeUploadedGeoJsonAsyncOptions {
  onRepairStart?: (message: string) => void | Promise<void>;
}

const geoJsonFormat = new GeoJSON();
const DUPLICATE_COORDINATE_NOTE = 'Removed duplicate consecutive coordinates from the AOI geometry.';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isFiniteCoordinate(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function sameRawPoint(a: unknown, b: unknown): boolean {
  return Array.isArray(a)
    && Array.isArray(b)
    && isFiniteCoordinate(a[0])
    && isFiniteCoordinate(a[1])
    && isFiniteCoordinate(b[0])
    && isFiniteCoordinate(b[1])
    && a[0] === b[0]
    && a[1] === b[1];
}

function readPoint(value: unknown, path: string): void {
  if (!Array.isArray(value) || value.length < 2) {
    throw new Error(`${path} must be a coordinate array with longitude and latitude`);
  }

  const lon = value[0];
  const lat = value[1];
  if (!isFiniteCoordinate(lon) || !isFiniteCoordinate(lat)) {
    throw new Error(`${path} must contain finite numeric coordinates`);
  }

  if (lon < -180 || lon > 180 || lat < -90 || lat > 90) {
    throw new Error(`${path} must use EPSG:4326 longitude/latitude coordinates`);
  }
}

function normalizeRingCoordinates(ring: unknown): { ring: unknown; cleaned: boolean } {
  if (!Array.isArray(ring) || ring.length < 2) {
    return { ring, cleaned: false };
  }

  const normalized: unknown[] = [];
  let cleaned = false;

  for (const coord of ring) {
    if (normalized.length > 0 && sameRawPoint(normalized[normalized.length - 1], coord)) {
      cleaned = true;
      continue;
    }
    normalized.push(coord);
  }

  return cleaned ? { ring: normalized, cleaned } : { ring, cleaned: false };
}

function normalizePolygonCoordinates(coordinates: unknown): { coordinates: unknown; cleaned: boolean } {
  if (!Array.isArray(coordinates)) {
    return { coordinates, cleaned: false };
  }

  let cleaned = false;
  const normalized = coordinates.map((ring) => {
    const result = normalizeRingCoordinates(ring);
    cleaned ||= result.cleaned;
    return result.ring;
  });

  return cleaned ? { coordinates: normalized, cleaned } : { coordinates, cleaned: false };
}

function normalizeMultiPolygonCoordinates(coordinates: unknown): { coordinates: unknown; cleaned: boolean } {
  if (!Array.isArray(coordinates)) {
    return { coordinates, cleaned: false };
  }

  let cleaned = false;
  const normalized = coordinates.map((polygon) => {
    const result = normalizePolygonCoordinates(polygon);
    cleaned ||= result.cleaned;
    return result.coordinates;
  });

  return cleaned ? { coordinates: normalized, cleaned } : { coordinates, cleaned: false };
}

function normalizeGeometryRecord(geometry: Record<string, unknown>): { geometry: Record<string, unknown>; cleaned: boolean } {
  if (geometry.type === 'Polygon') {
    const result = normalizePolygonCoordinates(geometry.coordinates);
    if (!result.cleaned) return { geometry, cleaned: false };
    return { geometry: { ...geometry, coordinates: result.coordinates }, cleaned: true };
  }

  if (geometry.type === 'MultiPolygon') {
    const result = normalizeMultiPolygonCoordinates(geometry.coordinates);
    if (!result.cleaned) return { geometry, cleaned: false };
    return { geometry: { ...geometry, coordinates: result.coordinates }, cleaned: true };
  }

  return { geometry, cleaned: false };
}

function normalizeGeoJsonDocumentValue(data: unknown): { data: unknown; cleaned: boolean } {
  if (!isRecord(data)) {
    return { data, cleaned: false };
  }

  if (data.type === 'Polygon' || data.type === 'MultiPolygon') {
    const result = normalizeGeometryRecord(data);
    return { data: result.geometry, cleaned: result.cleaned };
  }

  if (data.type === 'Feature') {
    if (!isRecord(data.geometry)) {
      return { data, cleaned: false };
    }

    const result = normalizeGeometryRecord(data.geometry);
    if (!result.cleaned) return { data, cleaned: false };
    return { data: { ...data, geometry: result.geometry }, cleaned: true };
  }

  if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
    let cleaned = false;
    const normalizedFeatures = data.features.map((feature) => {
      const result = normalizeGeoJsonDocumentValue(feature);
      cleaned ||= result.cleaned;
      return result.data;
    });

    if (!cleaned) return { data, cleaned: false };
    return { data: { ...data, features: normalizedFeatures }, cleaned: true };
  }

  return { data, cleaned: false };
}

function assertRingCoordinatesLike(ring: unknown, path: string): void {
  if (!Array.isArray(ring) || ring.length === 0) {
    throw new Error(`${path} must be a linear ring`);
  }

  ring.forEach((coord, index) => readPoint(coord, `${path}[${index}]`));
}

function assertPolygonCoordinatesLike(coordinates: unknown, path: string): void {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    throw new Error(`${path} must contain at least one linear ring`);
  }

  coordinates.forEach((ring, index) => assertRingCoordinatesLike(ring, `${path}[${index}]`));
}

function assertPolygonalGeometry(geometry: unknown, path: string): void {
  if (!isRecord(geometry)) {
    throw new Error(`${path} is missing a geometry object`);
  }

  if (geometry.type === 'Polygon') {
    assertPolygonCoordinatesLike(geometry.coordinates, `${path}.coordinates`);
    return;
  }

  if (geometry.type === 'MultiPolygon') {
    if (!Array.isArray(geometry.coordinates) || geometry.coordinates.length === 0) {
      throw new Error(`${path}.coordinates must contain at least one polygon`);
    }

    geometry.coordinates.forEach((polygon, index) =>
      assertPolygonCoordinatesLike(polygon, `${path}.coordinates[${index}]`),
    );
    return;
  }

  throw new Error(`${path} must be Polygon or MultiPolygon`);
}

function assertReadableByOpenLayers(data: Record<string, unknown>): void {
  try {
    const features = geoJsonFormat.readFeatures(data, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:4326',
    });

    if (features.length === 0) {
      throw new Error('GeoJSON did not produce any readable features');
    }

    for (const feature of features) {
      const geometry = feature.getGeometry();
      if (!geometry) {
        throw new Error('GeoJSON contains a feature without geometry');
      }

      const geometryType = geometry.getType();
      if (geometryType !== 'Polygon' && geometryType !== 'MultiPolygon') {
        throw new Error(`OpenLayers parsed an unsupported geometry type '${geometryType}'`);
      }

      const extent = geometry.getExtent();
      if (!extent.every((value) => Number.isFinite(value))) {
        throw new Error('GeoJSON produced a geometry with an invalid extent');
      }
    }
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'GeoJSON could not be parsed');
  }
}

function assertSupportedGeoJsonDocument(data: unknown): asserts data is Record<string, unknown> {
  if (!isRecord(data)) {
    throw new Error('Not a valid JSON object');
  }

  const docType = data.type;
  if (typeof docType !== 'string') {
    throw new Error("Missing 'type' property");
  }

  if (docType === 'Polygon' || docType === 'MultiPolygon') {
    assertPolygonalGeometry(data, 'geometry');
    assertReadableByOpenLayers(data);
    return;
  }

  if (docType === 'Feature') {
    assertPolygonalGeometry(data.geometry, 'feature.geometry');
    assertReadableByOpenLayers(data);
    return;
  }

  if (docType === 'FeatureCollection') {
    if (!Array.isArray(data.features) || data.features.length === 0) {
      throw new Error('FeatureCollection must contain at least one Feature');
    }

    data.features.forEach((feature, index) => {
      if (!isRecord(feature)) {
        throw new Error(`Feature[${index}] must be an object`);
      }
      assertPolygonalGeometry(feature.geometry, `Feature[${index}].geometry`);
    });

    assertReadableByOpenLayers(data);
    return;
  }

  throw new Error(`Unsupported GeoJSON type '${docType}'; need Polygon, MultiPolygon, Feature, or FeatureCollection`);
}

function formatUserFacingError(error: string): string {
  if (error.includes('EPSG:4326')) {
    return 'Coordinates are outside the valid range for WGS84 longitude/latitude. Please export the AOI as EPSG:4326 GeoJSON.';
  }
  if (error.includes('finite numeric coordinates')) {
    return 'The upload contains invalid coordinate values. Please export the AOI again and re-upload.';
  }
  if (error.includes('Unsupported GeoJSON type') || error.includes('must be Polygon or MultiPolygon')) {
    return 'Only Polygon and MultiPolygon geometries are supported for AOI uploads.';
  }
  if (error.includes('missing a geometry')) {
    return 'One or more features in the upload are missing geometry data.';
  }
  if (error.includes('Missing \'type\'')) {
    return 'The file is not valid GeoJSON because it is missing the required "type" property.';
  }
  if (error.includes('FeatureCollection must contain at least one Feature')) {
    return 'The uploaded FeatureCollection is empty.';
  }
  if (error.includes('did not produce any readable features') || error.includes('could not be parsed')) {
    return 'The file could not be read by the map as polygon GeoJSON. If QGIS accepts it, try exporting it again as GeoJSON in EPSG:4326.';
  }
  return error;
}

export function normalizeUploadedGeoJson(data: unknown): NormalizedUploadedGeoJsonResult {
  const notes: string[] = [];

  try {
    const normalized = normalizeGeoJsonDocumentValue(data);
    const normalizedData = normalized.data;

    if (normalized.cleaned) {
      notes.push(DUPLICATE_COORDINATE_NOTE);
    }

    assertSupportedGeoJsonDocument(normalizedData);

    return {
      geojson: normalizedData as Record<string, unknown>,
      error: null,
      cleaned: notes.length > 0,
      notes,
    };
  } catch (error) {
    return {
      geojson: null,
      error: formatUserFacingError(error instanceof Error ? error.message : 'Invalid GeoJSON'),
      cleaned: false,
      notes: [],
    };
  }
}

export function validateUploadedGeoJson(data: unknown): string | null {
  return normalizeUploadedGeoJson(data).error;
}

export async function normalizeUploadedGeoJsonAsync(
  data: unknown,
  _options: NormalizeUploadedGeoJsonAsyncOptions = {},
): Promise<NormalizedUploadedGeoJsonResult> {
  return normalizeUploadedGeoJson(data);
}
